import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {RelationshipType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BLUE_LIGHT, GRAY, GRAY_LIGHTER} from 'ui-library/Colors';
import {Header3} from 'ui-library/Headers';

import {VerticalSide} from './diagram-layout/diagramLayoutTypes';
import {getRelationshipDecoratorPixelPath, getRelationshipDecoratorTypes} from './relationshipPathDecorator';
import {stringifyPath} from './WarehouseDiagramRelationship';
import {warehouseDiagramTableRowStyles} from './WarehouseDiagramTableRow';

const LEGEND_FIRST_COLUMN_WIDTH = 70;
const RELATIONSHIP_START_POINT = {x: 0, y: 10};
const RELATIONSHIP_END_POINT = {x: LEGEND_FIRST_COLUMN_WIDTH, y: 10};

function SimpleRelationshipSVG(props: {relationshipType: RelationshipType}): JSX.Element {
  const {sourceDecoratorType, targetDecoratorType} = getRelationshipDecoratorTypes(props.relationshipType);

  const sourceDecoratorPath = getRelationshipDecoratorPixelPath(
    sourceDecoratorType,
    VerticalSide.RIGHT,
    RELATIONSHIP_START_POINT
  );
  const targetDecoratorPath = getRelationshipDecoratorPixelPath(
    targetDecoratorType,
    VerticalSide.LEFT,
    RELATIONSHIP_END_POINT
  );

  const sharedPolylineProps: React.SVGProps<SVGPolylineElement> = {
    strokeLinejoin: 'round',
    fill: 'none',
    stroke: BLUE_LIGHT,
    strokeWidth: 2,
  };

  return (
    <svg className={css(styles.simpleRelationshipSVG)}>
      <polyline {...sharedPolylineProps} points={stringifyPath(sourceDecoratorPath)} />
      <polyline {...sharedPolylineProps} points={stringifyPath(targetDecoratorPath)} />
      <polyline
        {...sharedPolylineProps}
        points={stringifyPath([RELATIONSHIP_START_POINT, RELATIONSHIP_END_POINT])}
      />
    </svg>
  );
}

function WarehouseDiagramLegend(): JSX.Element {
  return (
    <div className={css(styles.wrapper)}>
      <Header3>Legend</Header3>
      <table>
        <tbody>
          <tr>
            <td className={css(styles.cell)}>
              <SimpleRelationshipSVG relationshipType={RelationshipType.MANY_TO_ONE} />
            </td>
            <td className={css(styles.cell)}>Many-to-one relationship</td>
          </tr>
          <tr>
            <td className={css(styles.cell)}>
              <SimpleRelationshipSVG relationshipType={RelationshipType.ONE_TO_ONE} />
            </td>
            <td className={css(styles.cell)}>One-to-one relationship</td>
          </tr>
          <tr>
            <td className={css(styles.cell)}>
              <SimpleRelationshipSVG relationshipType={RelationshipType.UNKNOWN} />
            </td>
            <td className={css(styles.cell)}>Unknown relationship type</td>
          </tr>
          <tr>
            <td className={css(styles.cell)}>
              <span className={css(warehouseDiagramTableRowStyles.foreignKeyColumnName)}>blue_column</span>
            </td>
            <td className={css(styles.cell)}>Foreign key</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default React.memo(WarehouseDiagramLegend);

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    bottom: 15,
    right: 15,
    background: GRAY_LIGHTER,
    border: `1px solid ${GRAY}`,
    boxShadow: '0 0 2px #ccc',
    borderRadius: 4,
    padding: 10,
  },
  cell: {
    paddingRight: 15,
  },
  simpleRelationshipSVG: {
    width: LEGEND_FIRST_COLUMN_WIDTH,
    height: 17,
  },
});
